import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Box, TableSortLabel } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import JumboListNoDataPlaceholder from '@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder/JumboListNoDataPlaceholder';
import { useNavigate } from 'react-router-dom';

const TableComponent = ({
  columns,
  rows,
  page,
  count,
  Order,
  OrderBy,
  sortData,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  editAction,
  editActionId
}) => {
  const navigate = useNavigate();

  const [order, setOrder] = React.useState(Order ?? 'asc');
  const [orderBy, setOrderBy] = React.useState(OrderBy ?? '');

  React.useEffect(() => {
    if (sortData) sortData(order, orderBy);
  }, [order, orderBy, sortData]);

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  function getComparator(order, orderBy) {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
  }
  function stableSort(array, comparator) {
    const stabilizedThis = array?.map((el, index) => [el, index]);
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };
  const visibleRows = React.useMemo(
    () => stableSort(rows, getComparator(order, orderBy)),
    [rows, getComparator, order, orderBy],
  );

  return (
    <>
      {rows?.length === 0 ? (
        <JumboListNoDataPlaceholder />
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns?.map((column, index) => (
                    <TableCell
                      sortDirection={orderBy === column.id ? order : false}
                      key={`header-${index}`}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column?.sort ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={createSortHandler(column.id)}>
                          {column.label}
                          {orderBy === column.id ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                         typeof column.label === 'string' ? column.label : column?.label()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows?.map((row, index) => {
                  return (
                    <TableRow hover key={`row-${index}`}>
                      {columns?.map((column, columnIndex) => {
                        const value = row[column.id];
                        return (
                          <TableCell
                            key={`cell-${columnIndex}`}
                            align={column.align}
                            onClick={() => {
                              if (editAction && column.id !== 'actions') editAction(row)
                              if (editActionId && column.id !== 'actions') navigate(`${editActionId}${row?._id}`)
                            }}
                          >
                            {column.render
                              ? column.render(row, index)
                              : column.format && typeof value === 'number'
                                ? column.format(value)
                                : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {page && rowsPerPage && <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={count ?? rows?.length}
            rowsPerPage={rowsPerPage}
            page={page === 0 ? 0 : page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />}
        </Paper>
      )}
    </>
  );
};

export default TableComponent;
